import type { ToolUseItem } from '../../../../contracts/harness.types';
import {
  isRecord,
  readNumber,
  readString,
} from '../../../../contracts/projects/harness/toolResult';

import { friendlyToolError } from '../../../../data/projects/components/projectChatMessage.helpers';
import { toolResultText } from '../../harness/artifactFromTool';
import {
  MultiplayerHandoffBlock,
  type MultiplayerHandoffKind,
} from './MultiplayerHandoffBlock';
import { readToolData } from './shared/readers';
import { toFetchingStatus } from './status';

const MULTIPLAYER_KINDS = new Set<string>([
  'share_session',
  'unshare_session',
  'list_session_shares',
  'invite_team_member',
  'remove_team_member',
  'share_template',
  'unshare_template',
]);

export const isMultiplayerHandoffToolName = (name: string): boolean =>
  MULTIPLAYER_KINDS.has(name);

const formatAccessLevel = (level: string | undefined): string => {
  if (!level) return 'view';
  return level.split('_').join(' ');
};

const recipientNamesFromData = (data: Record<string, unknown> | undefined): string[] => {
  const raw = data?.recipients;
  if (!Array.isArray(raw)) return [];
  return raw
    .map((entry) => {
      if (!isRecord(entry)) return null;
      return readString(entry, 'name') ?? readString(entry, 'email');
    })
    .filter((name): name is string => Boolean(name));
};

const memberLinesFromData = (data: Record<string, unknown> | undefined): string[] => {
  const raw = data?.members;
  if (!Array.isArray(raw)) return [];
  return raw
    .map((entry) => {
      if (!isRecord(entry)) return null;
      const name = readString(entry, 'name') ?? readString(entry, 'email') ?? 'Colleague';
      const level = formatAccessLevel(readString(entry, 'level') ?? undefined);
      return `${name} · ${level}`;
    })
    .filter((line): line is string => Boolean(line));
};

const buildSummary = (
  kind: MultiplayerHandoffKind,
  item: ToolUseItem,
  data: Record<string, unknown> | undefined,
  status: 'pending' | 'completed' | 'error'
): { summary: string; details?: string[]; sessionId?: string } => {
  const input = item.input;
  const sessionId =
    readString(data, 'session_id') ?? readString(input, 'session_id') ?? undefined;

  switch (kind) {
    case 'share_session': {
      const level = formatAccessLevel(
        readString(data, 'level') ?? readString(input, 'level') ?? 'view'
      );
      const names = recipientNamesFromData(data);
      const count =
        readNumber(data, 'shared_count') ??
        (Array.isArray(input.clinician_ids) ? input.clinician_ids.length : names.length);
      if (status === 'pending') {
        return {
          summary: `Share session · ${level} access`,
          details:
            Array.isArray(input.clinician_ids) && input.clinician_ids.length > 0
              ? [`${input.clinician_ids.length} colleague(s)`]
              : undefined,
          sessionId,
        };
      }
      return {
        summary:
          names.length > 0
            ? `Shared with ${names.join(', ')} · ${level}`
            : `Shared with ${count} colleague${count === 1 ? '' : 's'} · ${level}`,
        sessionId,
      };
    }
    case 'unshare_session': {
      const name =
        readString(data, 'recipient_name') ??
        readString(data, 'recipient_email') ??
        'colleague';
      if (status === 'pending') {
        return { summary: 'Revoke session access', sessionId };
      }
      return { summary: `Revoked access for ${name}`, sessionId };
    }
    case 'list_session_shares': {
      const count = readNumber(data, 'member_count') ?? memberLinesFromData(data).length;
      const owner = isRecord(data?.owner) ? readString(data.owner, 'name') : undefined;
      const details = memberLinesFromData(data);
      if (status === 'pending') {
        return { summary: 'Checking who has access', sessionId };
      }
      return {
        summary:
          count === 0
            ? owner
              ? `No direct shares (owner: ${owner})`
              : 'No direct shares'
            : `${count} colleague${count === 1 ? '' : 's'} with access`,
        details: details.length > 0 ? details : undefined,
        sessionId,
      };
    }
    case 'invite_team_member': {
      const email =
        readString(data, 'email') ?? readString(input, 'email') ?? 'colleague';
      if (status === 'pending') {
        return { summary: `Invite ${email}` };
      }
      return { summary: `Invited ${email}` };
    }
    case 'remove_team_member': {
      if (status === 'pending') {
        return { summary: 'Remove team member' };
      }
      return { summary: 'Removed team member' };
    }
    case 'share_template': {
      if (status === 'pending') {
        return { summary: 'Share team template' };
      }
      return { summary: 'Shared team template' };
    }
    case 'unshare_template': {
      if (status === 'pending') {
        return { summary: 'Stop sharing template' };
      }
      return { summary: 'Stopped sharing template' };
    }
  }
};

export const renderMultiplayerHandoffToolUse = (
  item: ToolUseItem,
  onOpenSession?: (sessionId: string) => void
): JSX.Element | null => {
  if (!isMultiplayerHandoffToolName(item.name)) return null;
  const kind = item.name as MultiplayerHandoffKind;

  const status = toFetchingStatus(item.status);
  if (!status) return null;

  const blockStatus: 'pending' | 'completed' | 'error' =
    status === 'fetching' ? 'pending' : status === 'error' ? 'error' : 'completed';

  const data = readToolData(item);
  const { summary, details, sessionId } = buildSummary(kind, item, data, blockStatus);

  if (blockStatus === 'error') {
    return (
      <MultiplayerHandoffBlock
        kind={kind}
        status="error"
        summary={summary}
        error={friendlyToolError(toolResultText(item))}
        sessionId={sessionId}
      />
    );
  }

  return (
    <MultiplayerHandoffBlock
      kind={kind}
      status={blockStatus}
      summary={summary}
      details={details}
      sessionId={sessionId}
      onOpenSession={onOpenSession}
    />
  );
};
